import React from 'react';
import { Box, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Typography } from '@mui/material';
import { Description, DescriptionOutlined, HistoryOutlined, FileUpload } from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';
import { useTimezone } from '@uplift/contexts';
import { LightChip } from '../../LightChip';
import { createProviderLicenseCollabAgreement, getCollborationAgreementDownloadLink, License, LicenseCollabAgreement, patchProviderLicenseCollabAgreement, uploadFileToS3, UploadPurpose } from '@uplift/api';
import { Button } from '../../Button';
import { Modal, ModalActions, ModalContent } from '../../Modal';
import { Formik } from 'formik';
import { collabAgreementDefaultValues, CollabAgreementForm, CollabAgreementFormValues, collabAgreementValidationSchema } from '../forms/CollabAgreementForm';
import { usePDFDocument } from '../../molecules/Modals/patient-chart-review-modal/components/PDFViewer/usePDFDocument.hook';
import { PDFPage } from '../../molecules/Modals/patient-chart-review-modal/components/PDFViewer/PDFPage.component';
import { useAdminStateQuery, useProviderStatesSelectionMutation, useStateQuery, useUserQuery } from '@uplift/queries';

export type AdminLicenseStateRequirementsProps = {
  license: License;
  licenseCollabAgreements: LicenseCollabAgreement[];

  onUpdateCollabAgreementSuccess: () => Promise<void>;
};

export const AdminLicenseStateRequirements: React.VFC<AdminLicenseStateRequirementsProps> = ({
  license,
  licenseCollabAgreements,
  onUpdateCollabAgreementSuccess
}) => {
  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <CollabAgreementSection onUpdateCollabAgreementSuccess={onUpdateCollabAgreementSuccess} licenseCollabAgreements={licenseCollabAgreements} license={license} />
    </Box>
  );
};

const getCollabAgreementWithStatus = (activeCollabAgreement?: LicenseCollabAgreement) => {
  if (!activeCollabAgreement) {
    return { status: 'not_started' as const };
  }

  if (activeCollabAgreement.reviewedAt !== null) {
    return { status: 'reviewed' as const, agreement: activeCollabAgreement };
  }

  return { status: 'uploaded' as const, agreement: activeCollabAgreement };
};

type CollabAgreementStatus = ReturnType<typeof getCollabAgreementWithStatus>;

type CollabAgreementSectionProps = {
  licenseCollabAgreements: LicenseCollabAgreement[];
  license: License;
  onUpdateCollabAgreementSuccess: () => Promise<void>;
};

const CollabAgreementSection = ({ licenseCollabAgreements, license, onUpdateCollabAgreementSuccess }: CollabAgreementSectionProps) => {
  const activeCollabAgreement = licenseCollabAgreements.find(agreement => agreement.archivedAt === null);
  const [ modal, setModal ] = React.useState<'upload' | 'view_all' | 'view_document' | 'turn_matching_live'>();
  const [ menuAnchor, setMenuAnchor ] = React.useState<null | HTMLElement>(null);

  const collabAgreementWithStatus = getCollabAgreementWithStatus(activeCollabAgreement);

  const handleMarkAsReviewedSuccess = async () => {
    setModal('turn_matching_live');
    await onUpdateCollabAgreementSuccess();
  };

  return (
    <Paper variant="outlined" sx={{ p: 1, px: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="subtitle2">
            Collaborative Agreement
          </Typography>

          <CollabAgreementBadge agreement={activeCollabAgreement} />
          {licenseCollabAgreements.length > 0 && (
            <>
              <IconButton size="small" color="secondary" onClick={(e) => setMenuAnchor(e.currentTarget)}>
                <Description fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
              >
                {activeCollabAgreement && (
                  <MenuItem onClick={() => {
                    setMenuAnchor(null);
                    setModal('view_document');
                  }}
                  >
                    <ListItemIcon><DescriptionOutlined fontSize="small" /></ListItemIcon>
                    <ListItemText>View Document</ListItemText>
                  </MenuItem>
                )}
                {activeCollabAgreement && (
                  <MenuItem onClick={() => {
                    setMenuAnchor(null);
                    setModal('upload');
                  }}
                  >
                    <ListItemIcon><FileUpload fontSize="small" /></ListItemIcon>
                    <ListItemText>Replace Document</ListItemText>
                  </MenuItem>
                )}
                <Divider />
                <MenuItem onClick={() => {
                  setMenuAnchor(null);
                  setModal('view_all');
                }}
                >
                  <ListItemIcon><HistoryOutlined fontSize="small" /></ListItemIcon>
                  <ListItemText>View History</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <CollabAgreementActionButton
            status={collabAgreementWithStatus}
            license={license}
            onUpload={() => setModal('upload')}
            onMarkAsReviewedSuccess={handleMarkAsReviewedSuccess}
          />
        </Box>
      </Box>

      {modal === 'upload' && (
        <UploadCollabAgreementModal
          onClose={() => setModal(undefined)}
          licenseId={license.id}
          providerId={license.userId}
          stateId={license.stateId}
          onUpdateCollabAgreementSuccess={onUpdateCollabAgreementSuccess}
        />
      )}
      {modal === 'view_all' && (
        <CollabAgreementHistoryModal
          license={license}
          licenseCollabAgreements={licenseCollabAgreements}
          onClose={() => setModal(undefined)}
        />
      )}
      {modal === 'view_document' && activeCollabAgreement && (
        <ViewCollabAgreementModal
          agreement={activeCollabAgreement}
          providerId={license.userId}
          onClose={() => setModal(undefined)}
        />
      )}
      {modal === 'turn_matching_live' && (
        <TurnMatchingLiveModal
          license={license}
          onClose={() => setModal(undefined)}
        />
      )}
    </Paper>
  );
};

type CollabAgreementBadgeProps = {
  agreement: LicenseCollabAgreement | undefined;
};

const CollabAgreementBadge = ({ agreement }: CollabAgreementBadgeProps) => {
  const timezone = useTimezone();

  if (!agreement) {
    return <LightChip size="small" color="error" label="Not started" />;
  }

  if (agreement.reviewedAt !== null) {
    return <LightChip size="small" color="success" label="Reviewed" />;
  }

  const uploadedDate = CalendarDateService.date(agreement.uploadedAt).tz(timezone);

  return <LightChip size="small" color="warning" label={`Uploaded - ${uploadedDate.format(DateFormats.DEFAULT_DATE)} at ${uploadedDate.format(DateFormats['hh:mm A'])}`} />;
};

type CollabAgreementActionButtonProps = {
  status: CollabAgreementStatus;
  license: License;
  onUpload: () => void;
  onMarkAsReviewedSuccess: () => Promise<void>;
};

const CollabAgreementActionButton = ({ status, license, onUpload, onMarkAsReviewedSuccess }: CollabAgreementActionButtonProps) => {
  if (status.status === 'not_started') {
    return <Button variant="outlined" size="small" color="secondary" onClick={onUpload}>Upload agreement</Button>;
  }

  if (status.status === 'uploaded') {
    return (
      <MarkAsReviewedButton
        license={license}
        collabAgreementId={status.agreement.id}
        onMarkAsReviewedSuccess={onMarkAsReviewedSuccess}
      />
    );
  }

  return null;
};

type MarkAsReviewedButtonProps = {
  license: License;
  collabAgreementId: string;
  onMarkAsReviewedSuccess: () => Promise<void>;
};

const MarkAsReviewedButton: React.VFC<MarkAsReviewedButtonProps> = ({ license, collabAgreementId, onMarkAsReviewedSuccess }) => {
  const mutation = useMutation({
    mutationFn: async () => patchProviderLicenseCollabAgreement(license.id, collabAgreementId, { isReviewed: true }),
    onSuccess: async () => {
      await onMarkAsReviewedSuccess();
    },
  });

  return (
    <Button variant="outlined" size="small" color="secondary" onClick={mutation.mutateAsync} loading={mutation.isPending}>Mark as reviewed</Button>
  );
};

type TurnMatchingLiveModalProps = {
  license: License;
  onClose: () => void;
};

const TurnMatchingLiveModal: React.VFC<TurnMatchingLiveModalProps> = ({ license, onClose }) => {
  const { data: state } = useAdminStateQuery(license.stateId, { suspense: true });
  const mutation = useProviderStatesSelectionMutation(license.userId);

  const handleConfirm = async () => {
    await mutation.mutateAsync({ stateIds: [ license.stateId ], setLive: true });
    onClose();
  };

  return (
    <Modal open title="Turn Matching Live?" maxWidth="xs" showCloseIcon onClose={onClose}>
      <ModalContent>
        <Typography variant="body2">
          The collaborative agreement has been reviewed. Do you want to turn matching live for this provider in {state?.fullName}?
        </Typography>
      </ModalContent>
      <ModalActions>
        <Button variant="outlined" color="secondary" disabled={mutation.isPending} onClick={onClose}>Not now</Button>
        <Button variant="contained" color="secondary" loading={mutation.isPending} onClick={handleConfirm}>Turn live</Button>
      </ModalActions>
    </Modal>
  );
};

type ViewCollabAgreementModalProps = {
  agreement: LicenseCollabAgreement;
  providerId: string;
  onClose: () => void;
};

const ViewCollabAgreementModal: React.VFC<ViewCollabAgreementModalProps> = ({ agreement, providerId, onClose }) => {
  const { data: uploadedByUser } = useUserQuery(agreement.uploadedBy, { suspense: true });
  const { data: reviewedByUser } = useUserQuery(agreement.reviewedBy!, { suspense: true, enabled: agreement.reviewedBy !== null });
  const uploadedBy = uploadedByUser ? `${uploadedByUser?.firstName} ${uploadedByUser?.lastName}` : null;
  const reviewedBy = reviewedByUser ? `${reviewedByUser?.firstName} ${reviewedByUser?.lastName}` : null;
  const timezone = useTimezone();
  const pageElementRefs = React.useRef<HTMLDivElement[]>([]);

  const { data: downloadLink } = useQuery({
    queryKey: [ 'collabAgreementDownloadLink', providerId, agreement.id ],
    queryFn: () => getCollborationAgreementDownloadLink(providerId, agreement.id),
    select: (res) => res.data.downloadLink,
  });
  const { data: pdf } = usePDFDocument(downloadLink);
  const pages = React.useMemo(() => Array.from({ length: pdf?.numPages ?? 0 }).map((_, index) => index), [ pdf ]);

  const uploadedDate = CalendarDateService.date(agreement.uploadedAt).tz(timezone);
  const reviewedDate = agreement.reviewedAt ? CalendarDateService.date(agreement.reviewedAt).tz(timezone) : null;

  return (
    <Modal open title="Collaborative Agreement" maxWidth="xl" fullWidth showCloseIcon onClose={onClose}>
      <ModalContent>
        <Box display="flex" height="75vh" gap={0} overflow="hidden">
          <Box flex={1} overflow="auto" bgcolor="grey.100" p={2}>
            {pages.length > 0 ? (
              <Box display="flex" flexDirection="column" gap={2}>
                {pages.map((p) => (
                  <div
                    key={p}
                    ref={(el) => {
                      if (el) pageElementRefs.current[p] = el;
                    }}
                  >
                    {pdf && <PDFPage pdf={pdf} pageNumber={p} zoomAndDrag />}
                  </div>
                ))}
              </Box>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Typography color="textSecondary">Loading document...</Typography>
              </Box>
            )}
          </Box>

          <Divider orientation="vertical" />

          <Box width={300} flexShrink={0} overflow="auto" p={3}>
            <Box display="flex" flexDirection="column" gap={2.5}>
              <div>
                <LightChip size="small" color={agreement.archivedAt === null ? 'success' : 'default'} label={agreement.archivedAt === null ? 'Active' : 'Archived'} />
              </div>

              <Box>
                <Typography variant="caption" display="block">
                  Uploaded by {uploadedBy} on {uploadedDate.format(DateFormats.DEFAULT_DATE)} at {uploadedDate.format(DateFormats['hh:mm A'])}
                </Typography>
              </Box>

              {reviewedDate && (
                <Box>
                  <Typography variant="caption" sx={{ color: 'success.main' }} display="block" fontWeight={500}>
                    Reviewed by {reviewedBy} on {reviewedDate.format(DateFormats.DEFAULT_DATE)} at {reviewedDate.format(DateFormats['hh:mm A'])}
                  </Typography>
                </Box>
              )}

              <Divider />

              <Box>
                <Typography variant="caption" color="textSecondary" display="block">Expiration Date</Typography>
                <Typography variant="body2" color="textPrimary">{CalendarDateService.date(agreement.expirationDate, 'YYYY-MM-DD').format(DateFormats.DEFAULT_DATE)}</Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary" display="block">Supervising Physician</Typography>
                <Typography variant="body2" color="textPrimary">{agreement.physicianFirstName} {agreement.physicianLastName}</Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary" display="block">Supervisor NPI</Typography>
                <Typography variant="body2" color="textPrimary">{agreement.physicianNpi}</Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary" display="block">Supervisor Email</Typography>
                <Typography variant="body2" color="textPrimary">{agreement.physicianEmail}</Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary" display="block">Supervisor Phone</Typography>
                <Typography variant="body2" color="textPrimary">{agreement.physicianPhone || '‒'}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </ModalContent>
    </Modal>
  );
};

const getDefaultDate = (date: Date | string) =>
  CalendarDateService.date(date, DateFormats['MM/DD/YYYY'])
    .format(DateFormats['YYYY-MM-DD'])
    .toString();

export type UploadCollabAgreementModalProps = {
  onClose: () => void;
  licenseId: string;
  providerId: string;
  stateId: string;
  onUpdateCollabAgreementSuccess: () => Promise<void>;
};

export const UploadCollabAgreementModal: React.VFC<UploadCollabAgreementModalProps> = (props) => {
  const { data: state } = useStateQuery(props.stateId, { suspense: true });

  const onSubmit = async (values: CollabAgreementFormValues) => {
    if (!values.collaborationAgreement) return;

    const uploadedReq = await uploadFileToS3({
      providerId: props.providerId,
      purpose: UploadPurpose.collaborationAgreement,
      file: values.collaborationAgreement
    });

    await createProviderLicenseCollabAgreement(props.providerId, props.licenseId, {
      fileUploadId: uploadedReq.file_upload.id,
      ...values,
      expirationDate: getDefaultDate(values.expirationDate),
    });

    await props.onUpdateCollabAgreementSuccess();
    props.onClose();
  };

  return (
    <Formik
      initialValues={collabAgreementDefaultValues}
      validationSchema={collabAgreementValidationSchema}
      onSubmit={onSubmit}
    >
      {formik => (
        <Modal open title={`${state?.fullName ?? ''} Collaborative Agreement`} maxWidth="md" fullWidth showCloseIcon onClose={props.onClose}>
          <ModalContent>
            {console.log(formik)}
            <form id="collab-agreement-form" onSubmit={formik.handleSubmit}>
              <CollabAgreementForm />
            </form>
          </ModalContent>
          <ModalActions>
            <Button variant="outlined" color="secondary" disabled={formik.isSubmitting} onClick={props.onClose}>Cancel</Button>
            <Button variant="contained" color="secondary" disabled={formik.isSubmitting} type="submit" form="collab-agreement-form">Submit</Button>
          </ModalActions>
        </Modal>
      )}
    </Formik>
  );
};

type CollabAgreementHistoryModalProps = {
  license: License;
  licenseCollabAgreements: LicenseCollabAgreement[];
  onClose: () => void;
};

const CollabAgreementHistoryModal: React.VFC<CollabAgreementHistoryModalProps> = ({ license, licenseCollabAgreements, onClose }) => {
  const { data: state } = useAdminStateQuery(license.stateId, { suspense: true });

  return (
    <Modal open title={`Collaborative Agreement History - ${state?.fullName}`} maxWidth="md" fullWidth showCloseIcon onClose={onClose}>
      <ModalContent>
        <Box display="flex" flexDirection="column" gap={2}>
          {licenseCollabAgreements.map((agreement) => {
            return (
              <Paper key={agreement.id} variant="outlined" sx={{ p: 2 }}>
                <CollabAgreementHistoryItem agreement={agreement} />
              </Paper>
            );
          })}
        </Box>
      </ModalContent>
    </Modal>
  );
};

const CollabAgreementHistoryItem: React.VFC<{
  agreement: LicenseCollabAgreement;
}> = ({ agreement }) => {
  const { data: uploadedByUser } = useUserQuery(agreement.uploadedBy, { suspense: true });
  const { data: reviewedByUser } = useUserQuery(agreement.reviewedBy!, { suspense: true, enabled: agreement.reviewedBy !== null });
  const timezone = useTimezone();
  const uploadedDate = CalendarDateService.date(agreement.uploadedAt).tz(timezone);
  const uploadedBy = uploadedByUser ? `${uploadedByUser?.firstName} ${uploadedByUser?.lastName}` : null;
  const reviewedDate = agreement.reviewedAt ? CalendarDateService.date(agreement.reviewedAt).tz(timezone) : null;
  const reviewedBy = reviewedByUser ? `${reviewedByUser?.firstName} ${reviewedByUser?.lastName}` : null;

  return (
    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
      <Box display="flex" gap={1.5}>
        <DescriptionOutlined sx={{ color: 'primary.main', mt: 0.25 }} />
        <Box>
          <Typography variant="body2" fontWeight={600}>
            collaborative-agreement.pdf
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block">
            Uploaded by {uploadedBy}
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block">
            {uploadedDate.format(DateFormats.DEFAULT_DATE)} at {uploadedDate.format(DateFormats['hh:mm A'])}
          </Typography>
          {reviewedDate && (
            <Typography variant="caption" sx={{ color: 'success.main' }} display="block">
              Reviewed by {reviewedBy} on {reviewedDate.format(DateFormats.DEFAULT_DATE)} at {reviewedDate.format(DateFormats['hh:mm A'])}
            </Typography>
          )}
        </Box>
      </Box>
      <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
        <LightChip size="small" color={agreement.archivedAt === null ? 'success' : 'default'} label={agreement.archivedAt === null ? 'Active' : 'Archived'} />
        {/* <Typography
          variant="caption"
          sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 500 }}
        >
          Download Doc
        </Typography> */}
      </Box>
    </Box>
  );
};

// phase 2 - exhibit signature requirements (ignore for now)

// type ExhibitSignatureSectionProps = {
//   license: License;
// };

// const ExhibitSignatureSection = ({ license }: ExhibitSignatureSectionProps) => {
//   const exhibitSigned = license.exhibitSignatureProvided;

//   return (
//     <Paper variant="outlined" sx={{ p: 1, px: 2 }}>
//       <Box display="flex" justifyContent="space-between" alignItems="center">
//         <Box display="flex" alignItems="center" gap={1}>
//           <Typography variant="subtitle2">
//             State Exhibit
//           </Typography>

//           {/* {badge && (
//             <LightChip
//               size="small"
//               color={badge.color}
//               label={badge.label}
//             />
//           )} */}
//         </Box>
//         <FormControlLabel
//           sx={{ mr: 0 }}
//           control={<Switch checked={exhibitSigned} name="exhibitSigned" color="secondary" />}
//           label="Signed"
//         />
//       </Box>
//     </Paper>
//   );
// };
